/**
 * Password hashing utility using Web Crypto API
 * This provides secure password hashing without external dependencies
 */

/**
 * Hash a password using PBKDF2
 * @param password - Plain text password
 * @returns Hashed password as base64 string
 */
export async function hashPassword(password: string): Promise<string> {
  // Convert password to ArrayBuffer
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  
  // Generate a random salt
  const salt = crypto.getRandomValues(new Uint8Array(16));
  
  // Import the password as a key
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    data,
    'PBKDF2',
    false,
    ['deriveBits']
  );
  
  // Derive bits using PBKDF2 (this is extractable)
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000, // High iteration count for security
      hash: 'SHA-256'
    },
    keyMaterial,
    256 // 256 bits = 32 bytes
  );
  
  // Convert derived bits to array
  const hashArray = Array.from(new Uint8Array(derivedBits));
  
  // Combine salt and hash, then encode as base64
  const combined = new Uint8Array(salt.length + hashArray.length);
  combined.set(salt, 0);
  combined.set(new Uint8Array(hashArray), salt.length);
  
  // Convert to base64 for storage
  const base64 = btoa(String.fromCharCode(...combined));
  return base64;
}

/**
 * Verify a password against a hash
 * @param password - Plain text password to verify
 * @param hash - Stored hash (base64 string with salt)
 * @returns True if password matches
 */
export async function verifyPassword(password: string, hash: string): Promise<boolean> {
  try {
    // Check if crypto.subtle is available (required for Web Crypto API)
    if (!crypto || !crypto.subtle) {
      console.error('Web Crypto API (crypto.subtle) is not available. This may be due to:');
      console.error('1. Not running in a secure context (HTTPS required)');
      console.error('2. Android WebView configuration issue');
      console.error('3. Browser compatibility issue');
      // Fallback: return false but log the issue
      throw new Error('Web Crypto API not available');
    }

    // Decode base64 hash
    const combined = Uint8Array.from(atob(hash), c => c.charCodeAt(0));
    
    // Extract salt (first 16 bytes)
    const salt = combined.slice(0, 16);
    const storedHash = combined.slice(16);
    
    // Convert password to ArrayBuffer
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    
    // Import the password as a key
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      data,
      'PBKDF2',
      false,
      ['deriveBits']
    );
    
    // Derive bits using same parameters
    const derivedBits = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      256 // 256 bits = 32 bytes
    );
    
    // Convert derived bits to array
    const hashArray = Array.from(new Uint8Array(derivedBits));
    
    // Compare hashes
    if (hashArray.length !== storedHash.length) {
      return false;
    }
    
    for (let i = 0; i < hashArray.length; i++) {
      if (hashArray[i] !== storedHash[i]) {
        return false;
      }
    }
    
    return true;
  } catch (error: any) {
    console.error('Error verifying password:', error);
    console.error('Error details:', {
      message: error?.message,
      name: error?.name,
      stack: error?.stack
    });
    // Re-throw to allow caller to handle the error appropriately
    throw error;
  }
}

